/* eslint-disable no-underscore-dangle */
import 'whatwg-fetch';

import React from 'react';
import ReactDOM from 'react-dom';
import get from 'lodash/get';
import queryString from 'query-string';
import * as serviceWorker from './serviceWorker';
import Browser from './util/Browser';
import Payload from './util/Payload';
import { state } from './app';
import AdsControl from './util/AdsControl';
import { error } from 'logrocket';

function didFinishShow() {
  console.warn('didFinishShow');
  window.server.notifySdk('didFinishShow');
}

/**
 * The engagement app should call didFinishLoad once it has finished loading.
 * This function indicates to the client that the app is finished loading.
 */
function didFinishLoad() {
  // Guard against duplicate calls - prevent multiple notifications to SDK
  if (state.loaded) {
    console.warn('didFinishLoad - already called, skipping duplicate');
    return;
  }

  // client should launch all http requests in a browser from now on.
  console.log(
    'Notifying SDK of didFinishLoad(): loaded all iframes, notifying the SDK to launch requests in browser from now on'
  );

  console.warn('didFinishLoad');
  // previously - window.location.href = Browser.generateCustomUrl("http-default-safari")
  window.server.notifySdk('didFinishLoad');
  state.loaded = true;

  // for in-hand, let's skip waiting for foregrounding and being ready and what-not, shall we?
  if (Payload.isInHand(state.serverPayload)) {
    console.log(
      "The payload IS inHand, so we're calling prepareToShow() here under the assumption that the app is foreground and ready."
    );
    window.prepareToShow();
  }
}

/**
 * The engagement app should call didFinishHide once it has been fully removed from view.
 * This function indicates to the client that the app has been hidden.
 *
 * In iOS the OM SDK session is finished in the if-statement handling this callback.
 * Shortly afterward, the iOS SDK will call back in with window.unload().
 * The actual window doesn't get hidden till unload() responds with `m2m://interstitial/didFinishUnload`,
 * therefore at this vector we should install, for iOS only, a 1.5s to allow OM SDK to finish the session.
 *
 * Where and when the Android version stops the OM SDK session is a bit more complicated.
 * It happens in the M2MWebView.unload() method, which calls window.unload() 1.5s later.
 * The notion here was to give IAB enough time to fire off the `sessionFinish` event type.
 *
 */
function didFinishHide(target) {
  console.warn('didFinishHide', target);

  // Best-effort unload attribution marker (used to classify unload reason/confidence)
  try {
    window.__e2UnloadAttribution = window.__e2UnloadAttribution || { version: 1, events: {} };
    window.__e2UnloadAttribution.events.E2_DID_FINISH_HIDE = {
      atMs: Date.now(),
      atIso: new Date().toISOString(),
      target: target || null,
    };
  } catch (e) {
    // no-op
  }

  // Option B pattern - detailed log with impression_id
  // perfTime is automatically added by window.server.log()
  const ads = Payload.findAdsInPayload(state.serverPayload);
  if (ads.length > 0) {
    window.server.log({
      tag: 'E2_DID_FINISH_HIDE',
      fn: 'didFinishHide',
      msg: 'didFinishHide() called',
      inmarketParams: {
        m2m_impr_id: ads[0].impression_id,
      },
      target: target || null,
    });
  } else {
    // Fallback if no ads available
    window.server.log({
      tag: 'E2_DID_FINISH_HIDE',
      fn: 'didFinishHide',
      msg: 'didFinishHide() called',
      target: target || null,
    });
  }

  // surveys are set as complete and their own and an update of engagement time should not occur
  for (let index = 0; index < ads.length; index += 1) {
    // we can't assume that the first pixel is the m2m one... it could be a thinknear pel.
    const m2mPels = ads[index].tracking_pixels.filter(p =>
      p.match(/inmarket\.com\/advertiser\/pixel/)
    );
    if (m2mPels.length === 0) {
      continue;
    }
    const pixel = m2mPels[0];
    const pixelclose = `${pixel}&pixelclose=1`;
    // https://matt-m2m.inmarket.com/advertiser/pixel?m2m_impr_id=60063&m2m_impr_hash=96226d9c4ef6ba3c50dae6feb187e24560965c5b&m2m_server_rels=20150924&pixelclose=1
    if (typeof pixel !== 'undefined' && Browser.validURL(pixel)) {
      const s = document.createElement('img');
      s.style.cssText = `position: absolute;top: -9999px;left: -9999px;`;
      s.width = 0;
      s.height = 0;
      s.src = pixelclose;
      console.log('window.didFinishHide() addingtopage - pixel');
      console.log(s.src);
      Browser.scriptContainerNode().appendChild(s);
    }
  }

  if (state.adsControl.nextAd()) {
    return;
  }
  // previously - window.location.href = Browser.generateCustomUrl("close")
  let url = 'didFinishHide';
  if (typeof target !== 'undefined') {
    url = `${url}?url=${target}`;
  }
  setTimeout(() => {
    window.server.notifySdk(url);
  }, 700);
}

function bundleLoaded() {
  console.log('>>>>> window.bundleLoaded()');
  const parsed = queryString.parseUrl(window.location.href);
  const relsNo = get(parsed, 'query.m2m_relsno');
  const sendDidFinishBundleLoad = relsNo % 2 === 0 && relsNo >= 306;
  if (sendDidFinishBundleLoad) {
    window.server.notifySdk('didFinishBundleLoad');
  }
  window.server.adsControl.maybeContinue(window.preload);
  console.log('E2 Bundle Loaded with m2m_reslno', relsNo);
}

function addParams(appendMeta) {
  let metaData = {
    sessionId: window.server.sessionId,
    device: window.server.device,
    performance:
      typeof performance === 'object' && typeof performance.now === 'function'
        ? Math.round(performance.now() - window.initPerf)
        : 0,
    displayPerf:
      typeof performance === 'object' && typeof performance.now === 'function' && window.displayPerfPoint
        ? Math.round(performance.now() - window.displayPerfPoint)
        : 0,
    focus: document.hasFocus(),
  };
  window.server.lastLog = performance.now();
  if (typeof appendMeta === 'object') {
    metaData = Object.assign(appendMeta, metaData);
  }
  return JSON.stringify(metaData);
}

function notifySdk(path, scheme) {
  console.log('>>>>> window.notifySDK()');
  console.log('path');
  console.log(path);
  console.log('scheme');
  console.log(scheme);

  // Log E2 → SDK communication
  // perfTime is automatically added by window.server.log()
  window.server.foreachAd(ad => {
    window.server.log({
      tag: 'E2_NOTIFY_SDK',
      fn: 'notifySdk',
      msg: `E2 notifying SDK: ${path}`,
      inmarketParams: {
        m2m_impr_id: ad.impression_id,
      },
      sdkCallback: path,
      scheme: scheme || null,
    });
  });

  const newSrc = Browser.generateCustomUrl(path, scheme);
  const payload = { url: newSrc };
  const parsed = queryString.parseUrl(window.location.href);
  const relsNo = get(parsed, 'query.m2m_relsno');
  const appendToBodyOrRoot = (element) => {
    if (get(document, 'body')) {
      Browser.scriptContainerNode().appendChild(element);
    }
  };
  if (Browser.useMockM2M()) {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = Browser.generateCustomUrl(path, Browser.mockM2MEndpoint());
    appendToBodyOrRoot(script);
  } else if (false && get(window, 'M2MWebView.postMessage') && relsNo > 335) {
    // Android >3.35; there's a bug in 3.35, so check for whatever version we fix this in.
    console.warn(`notifySdk M2MWebView.postMessage w/ ${payload.url}`);
    window.M2MWebView.postMessage(JSON.stringify(payload));
  } else if (get(window, 'webkit.messageHandlers.m2mJsHandler')) {
    // iOS after a certain version
    console.warn(`notifySdk m2mJsHandler.postMessage w/ ${payload.url}`);
    console.warn(payload);
    window.webkit.messageHandlers.m2mJsHandler.postMessage(payload);
  } else {
    console.warn(`notifySdk iframe w/ ${payload.url}`);
    const ifr = document.createElement('iframe');
    ifr.setAttribute('style', 'width:0;height:0;position:absolute;border:none;z-index:-9999');
    ifr.src = payload.url;
    ifr.onerror = function onerror(e) {
      console.warn(`Error in notifySdk on ${payload.url}`);
      return false;
    };
    ifr.width = 0;
    ifr.height = 0;
    appendToBodyOrRoot(ifr);
  }
}

const reloadForAdsControl = () => {
  console.log('>>>>> window.reloadForAdsControl()');
  state.hasInitialized = false;
  state.loaded = false;
  let celtra, imHtml;
  Browser.scriptContainerNode().innerHTML = '';
  // remove celtra node if it exists
  if ((celtra = Browser.celtraContainerNode())) {
    celtra.parentNode.removeChild(celtra);
  }
  // remove imHtml node if it exists
  if ((imHtml = Browser.imHtmlNode())) {
    imHtml.parentNode.removeChild(imHtml);
  }
  // removes all "celtra nodes" which are any divs with the class 'notranslate'
  Browser.clearCeltraNodes();

  // removes the html element with #root id and creates a new one
  // this unmounts the entire react app
  if (ReactDOM.unmountComponentAtNode(Browser.domContainerNode())) {
    console.log('window.reloadForAdsControl() - unmounting react app')
    document.body.removeChild(Browser.domContainerNode());
    const newEl = document.createElement('div');
    newEl.id = 'root';
    document.body.appendChild(newEl);
  }
  // remove all third party pels
  const thirdPelsIncludes = document.querySelectorAll('script[src*="third_party_impr_pels.js"][m2m]');
  if (thirdPelsIncludes) {
    for (let i = 0; i < thirdPelsIncludes.length; i += 1) {
      thirdPelsIncludes.item(i).parentElement.removeChild(thirdPelsIncludes.item(i));
    }
  }
  bundleLoaded();
};

const setupWindowServer = () => {
  console.log('>>>>> window.setupWindowServer()');
  const enableServerLogs = Browser.shouldSendEngagementLog();
  console.log('>>>>> enableServerLogs:', enableServerLogs, 'URL:', window.location.href);
  const adsControl = new AdsControl(reloadForAdsControl, localStorage);

  // Test log to verify logging is working (only if logging enabled)
  if (enableServerLogs) {
    console.log('✅ E2: Logging is ENABLED - logs will be sent to server');
  } else {
    console.warn('⚠️ E2: Logging is DISABLED - add ?m2m_server_logging=1 to URL to enable');
  }

  // Sequence number generator for log ordering
  let sequenceNumber = 0;
  const getNextSequenceNumber = () => {
    sequenceNumber += 1;
    return sequenceNumber;
  };

  // PERFORMANCE FIX: Cache log URL to avoid recalculating on every log call
  let cachedLogUrl = null;
  const getLogUrl = () => {
    if (!cachedLogUrl) {
      cachedLogUrl = `${Browser.getApiHost()}/engagement/log?src=app.js`;
    }
    return cachedLogUrl;
  };

  window.server = {
    sessionId: Browser.isLocal() ? 1 : Math.floor(Math.random() * (99999999 - 1)) + 1,
    device: Browser.isMobile(true),
    lastLog: typeof performance === 'object' ? performance.now() : null,
    moat: null,
    currentAd: adsControl.currentAd,
    adsControl,
    addParams,
    notifySdk,
    getNextSequenceNumber,
    logRequestUrl(url) {
      if (Browser.isUrlMoatPixel(url)) {
        // on some creatives, the E2Image Proxy class (in third_party_impr_pels.js)
        // doesn't work.  For these this serves as a backstop...
        // in newer m2m sdk client versions that support calls into this method
        console.log(`logRequestUrl() - setting moat url to ${url}`);
        this.moat = url;
      }
      // perfTime is automatically added by window.server.log()
      window.server.foreachAd(ad => {
        window.server.log({
          tag: 'LOG_REQUEST_URL',
          url,
          inmarketParams: { m2m_impr_id: ad.impression_id },
        });
      });
    },
    log(data) {
      let localData = data;
      try {
        const e2Version = Browser.getParameterValue('v');
        // Get next sequence number - use closure function directly for reliability
        const sequenceNum = getNextSequenceNumber();
        // Add perfTime automatically if not already provided (for sequencing)
        // If perfTime is already in data, preserve it (it might be more accurate for that specific event)
        const perfTime = localData.perfTime !== undefined
          ? localData.perfTime
          : (typeof performance !== 'undefined' && performance.now ? Math.round(performance.now() * 100) / 100 : null);

        const newData = {
          src: 'app.js',
          e2Version,
          ...localData,
          sequence: sequenceNum,
          perfTime: perfTime, // Always ensure perfTime is present for sequencing
          timestamp: new Date().toISOString(),
          meta_data: this.addParams(localData.meta_data),
        };
        localData = newData;
      } catch (e) {
      }

      if (!enableServerLogs) {
        console.warn('E2: Logging disabled - m2m_server_logging=1 not in URL');
        return;
      }
      if (localData === '' || localData === '{}') {
        console.warn('E2: Logging skipped - empty data');
        return;
      }

      function doPost() {
        try {
          // PERFORMANCE FIX: Removed wasteful JSON.parse() - localData is already an object
          // Removed excessive console.log calls in production path
          // Only log in development or when explicitly debugging
          if (process.env.NODE_ENV === 'development') {
            console.log('E2: Logging to server:', localData.tag || 'unknown');
          }

          // PERFORMANCE FIX: Use cached URL instead of recalculating Browser.getApiHost() every time
          const url = getLogUrl();
          const xhr = window.XMLHttpRequest
            ? new XMLHttpRequest()
            : new window.ActiveXObject('Microsoft.XMLHTTP');
          xhr.open('POST', url);

          // PERFORMANCE FIX: Use onload/onerror instead of onreadystatechange for better performance
          xhr.onload = function () {
            if (xhr.status >= 200 && xhr.status < 300) {
              if (process.env.NODE_ENV === 'development') {
                console.log('E2: Log posted successfully:', xhr.status);
              }
            } else {
              console.warn('E2: Log post failed:', xhr.status, xhr.statusText);
            }
          };
          xhr.onerror = function () {
            console.warn('E2: Log post network error');
          };

          xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.send(JSON.stringify(localData));
        } catch (e) {
          console.error('Error posting e2 log:', e);
        }
      }

      if (!state.loaded) {
        const k = setInterval(() => {
          if (state.loaded) {
            doPost();
            clearInterval(k);
          }
        }, 100);
      } else {
        doPost();
      }
    },
    foreachAd(fn) {
      try {
        if (typeof state.serverPayload.data.actions !== 'undefined') {
          var { actions } = state.serverPayload.data;
        } else {
          return;
        }
        for (const i in actions) {
          if (typeof actions[i].type !== 'undefined' && actions[i].type !== 'queue_interstitial') {
            continue;
          }
          const { ads } = actions[i];
          for (const j in ads) {
            if (typeof ads[j].impression_id === 'undefined') {
              continue;
            }
            fn(ads[j]);
          }
        }
      } catch (e) {
      }
    },
    logForImprIds(msg, additionalData = {}) {
      // perfTime is automatically added by window.server.log()
      window.server.foreachAd(ad => {
        window.server.log({
          tag: msg,
          inmarketParams: { m2m_impr_id: ad.impression_id },
          ...additionalData,
        });
      });
    },
  };
};

const setupWindowErrorFunction = () => {
  // Rate-limit global error telemetry so we don't spam logs on pathological pages.
  // This is especially important for resource errors and unhandled rejections, which can repeat.
  const createRateLimiter = (maxPerSession = 5) => {
    let count = 0;
    return () => {
      count += 1;
      return count <= maxPerSession;
    };
  };

  const allowAppError = createRateLimiter(10);
  const allowUnhandledRejection = createRateLimiter(10);
  const allowResourceError = createRateLimiter(10);

  const logIfPossible = (data) => {
    try {
      if (window.server && typeof window.server.log === 'function') {
        window.server.log(data);
      }
    } catch (e) {
      // no-op
    }
  };

  const windowErrorFunction = function windowErrorFunction(e) {
    const payload = typeof state.serverPayload !== 'undefined' ? state.serverPayload : null;
    // we include payload here, because a specific impression may not be at fault...
    // server side, we'll log to all impressions in the payload.
    // it's important that this APP_ERROR be the only APP_ERROR with payload as a property
    let o = { wasErrorEvent: false };
    if (typeof e === 'object' && e instanceof ErrorEvent) {
      o.wasErrorEvent = true;
      for (const p in e) {
        if (typeof e[p] === 'object' || typeof e[p] === 'function') {
          continue;
        }
        o[p] = e[p];
      }
    } else {
      o = e;
    }
    if (!allowAppError()) return;

    // perfTime is automatically added by window.server.log()
    logIfPossible({
      tag: 'APP_ERROR',
      error: o,
      payload,
    });
  };

  // 1) JS runtime errors (ErrorEvent)
  window.addEventListener('error', windowErrorFunction);

  // 2) Resource load errors (script/css/img/iframe). These are "error" events too,
  // but they don't surface as ErrorEvent and are only reliably caught in capture phase.
  window.addEventListener(
    'error',
    (evt) => {
      try {
        if (!allowResourceError()) return;
        const target = evt && evt.target ? evt.target : null;
        const tagName = target && target.tagName ? String(target.tagName).toLowerCase() : null;
        const src = target && typeof target.src === 'string' ? target.src : null;
        const href = target && typeof target.href === 'string' ? target.href : null;

        // Only log known resource targets to avoid double-logging JS ErrorEvents.
        if (!tagName || !['script', 'link', 'img', 'iframe'].includes(tagName)) return;

        logIfPossible({
          tag: 'APP_RESOURCE_ERROR',
          msg: 'Resource failed to load',
          resource: {
            tagName,
            src,
            href,
          },
        });
      } catch (e) {
        // never break the app
      }
    },
    true
  );

  // 3) Unhandled promise rejections (common source of "silent" fatal flows)
  window.addEventListener('unhandledrejection', (evt) => {
    try {
      if (!allowUnhandledRejection()) return;
      const r = evt ? evt.reason : null;
      const isErrorObj = r && typeof r === 'object' && (r instanceof Error);
      logIfPossible({
        tag: 'APP_UNHANDLED_REJECTION',
        msg: 'Unhandled promise rejection',
        error: isErrorObj
          ? { name: r.name, message: r.message, stack: r.stack }
          : { reason: typeof r === 'string' ? r : JSON.stringify(r) },
      });
    } catch (e) {
      // never break the app
    }
  });

  // 4) Connectivity hints (not definitive, but useful for "connection issues")
  window.addEventListener('offline', () => {
    try {
      logIfPossible({ tag: 'APP_OFFLINE', msg: 'Browser reported offline' });
    } catch (e) { }
  });
  window.addEventListener('online', () => {
    try {
      logIfPossible({ tag: 'APP_ONLINE', msg: 'Browser reported online' });
    } catch (e) { }
  });
};

const setupServiceRegistration = () => {
  // If you want your app to work offline and load faster, you can change
  // unregister() to register() below. Note this comes with some pitfalls.
  // Learn more about service workers: http://bit.ly/CRA-PWA
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    console.log('Service worker will be registered.');
  } else {
    console.log(
      `Service worker will not be registered.  process.env.NODE_ENV=${process.env.NODE_ENV}`
    );
  }
  serviceWorker.register({
    onUpdate: registration => {
      console.log('serviceWorker updated! registration:', registration);
    },
    onSuccess: registration => {
      console.log('serviceWorker registered! registration:', registration);
    },
  });
};

const handlePayloadAndPreview = () => {
  // //////////////////////////////////////////////////////////////////////////////
  // for testing in Chrome mobile emulator from desktop
  console.log('>>>>> window.handlePayloadAndPreview()');
  let payload = Browser.getParameterValue('payload');
  if (
    payload === 'preview' ||
    (!window.server.adsControl.isContinuation() &&
      (!Browser.isMobile() || payload) &&
      !Browser.useMockM2M())
  ) {
    payload = payload || 'spencer';
    console.log('loading payload', payload);
    fetch(`/demo/${payload}.json`)
      .then(r => r.json())
      .then(res => {
        // M2M-CMS preview, populate fields based on query params.
        if (payload === 'preview') {
          if (!get(res, 'data')) {
            return false;
          }
          // Define ad interstitial.
          if (get(res, 'data.actions[0].ads[0]')) {
            let html = '';
            try {
              html = atob(Browser.getParameterValue('interstitial_html'));
            } catch (e) { }
            res.data.actions[0].ads[0] = {
              impression_id: Math.floor(Math.random() * 20000000),
              url: Browser.getParameterValue('url'),
              tracking_pixels: [],
              click_through_url: Browser.getParameterValue('click_through_url'),
              type: Browser.getParameterValue('type'),
              interstitial_html: html,
              click_action: Browser.getParameterValue('click_action'),
              height: Browser.getParameterValue('height'),
              width: Browser.getParameterValue('width'),
              scrollable: Browser.getParameterValue('scrollable'),
              preview: true,
            };
          }
          // Define ad icon, defaults to Checkpoints.
          if (get(res, 'data.fields.publisher')) {
            res.data.fields.publisher.engagement_app_icon = `https://s3.amazonaws.com/ad.inmarket.com/e2/${Browser.getParameterValue(
              'engagement_app_icon'
            )}`;
          }
        }

        console.log('data from server: ', res);

        // Leaving this in because the PHP backend still uses this `body.type`
        if (res.type && res.type === 'sniff-and-tell') {
          window.preload(res.preload);

          // delay to let preload finish preloading
          setTimeout(window.rangingResults(res.rangingResults), 1500);
        } else {
          window.preload(res);
        }
      })
      .catch(err => {
        console.log('error loading sample data');
        console.log(err);
      });
  }
};

// In the event of a deeplink click event, this function
// will be called to track the click just before the app
// follows the deeplink.
function setupClickTrackCaller() {
  window.callToTrackLink = () => {
    const { clickTrackLink } = window;
    if (clickTrackLink) {
      fetch(clickTrackLink)
        .then(response => {
          console.log('clickTrackLink - Response')
          console.log(response)
          if (response.ok || response.status === 302) {
            return true;
          } else {
            throw new Error('clickTrackLink - Error returned by clickTrackLink');
          }
        })
        .then(data => {
          console.log(data)
          console.log('clickTrackLink - Success');
        })
        .catch(error => {
          console.error(error);
          console.error('clickTrackLink - Error calling clickTrackLink');
        });
    } else {
      console.log('clickTrackLink - No clickTrackLink to call');
    }
  };
}

function didFinishStartHide() {
  console.warn('didFinishStartHide');

  // Best-effort unload attribution marker (used to classify unload reason/confidence)
  try {
    window.__e2UnloadAttribution = window.__e2UnloadAttribution || { version: 1, events: {} };
    window.__e2UnloadAttribution.events.E2_DID_FINISH_START_HIDE = {
      atMs: Date.now(),
      atIso: new Date().toISOString(),
    };
  } catch (e) {
    // no-op
  }

  // Option B pattern - detailed log with impression_id
  // perfTime is automatically added by window.server.log()
  const ads = Payload.findAdsInPayload(state.serverPayload);
  if (ads.length > 0) {
    window.server.log({
      tag: 'E2_DID_FINISH_START_HIDE',
      fn: 'didFinishStartHide',
      msg: 'didFinishStartHide() called',
      inmarketParams: {
        m2m_impr_id: ads[0].impression_id,
      },
    });
  } else {
    // Fallback if no ads available
    window.server.log({
      tag: 'E2_DID_FINISH_START_HIDE',
      fn: 'didFinishStartHide',
      msg: 'didFinishStartHide() called',
    });
  }

  window.server.notifySdk('didFinishStartHide');
}

export {
  setupWindowServer,
  setupWindowErrorFunction,
  didFinishHide,
  didFinishStartHide,
  didFinishLoad,
  didFinishShow,
  bundleLoaded,
  setupServiceRegistration,
  handlePayloadAndPreview,
  setupClickTrackCaller,
};
